<template>
  <div
  v-if="visible"
  @keydown.esc="hidePreview"
  class="ModalPreview">
    <div class="ModalPreview__bar">
      <div class="ModalPreview__bar--left">
        <i
        class="el-breadcrumb__separator el-icon-back ModalPreview__back-icon"
        @click="hidePreview">
        </i>
        <div class="ModalPreview__title">
          <span class="ModalPreview__name">
            {{file.name}}
          </span>
          <span class="ModalPreview__info">
            {{ fileSize }}, {{ modificationDate }}
          </span>
        </div>
      </div>
      <div class="ModalPreview__bar--right">
        <FileActions
        :file="file"/>
      </div>
    </div>
    <div
    class="ModalPreview__content"
    v-loading="loading"
    v-if="file.presignedUrl">
      <audio
      v-if="file.type === 1"
      class="ModalPreview__audio"
      autoplay
      controls
      @error="errorMessage"
      :src="file.presignedUrl"/>
      <video
      v-if="file.type === 2"
      class="ModalPreview__video"
      autoplay
      controls
      @error="errorMessage"
      :src="file.presignedUrl"/>
      <div
      v-if="file.type === 3"
      v-viewer="viewerOptions">
        <img
        @load="loaded"
        @error="errorMessage"
        class="ModalPreview__image"
        :src="file.presignedUrl"
        alt="Thumbnail"
        hidden>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Message } from 'element-ui'

import SizeMixin from '@/components/mixins/Size.mixin'

export default {
  name: 'ModalPreview',

  mixins: [
    SizeMixin
  ],

  data () {
    return {
      loading: false,
      viewerOptions: {
        movable: true,
        inline: true,
        button: true,
        navbar: false,
        title: false,
        tooltip: false,
        zoomable: true,
        rotatable: true,
        scalable: true,
        transition: true,
        fullscreen: true,
        keyboard: true,
        toolbar: {
          zoomIn: 1,
          zoomOut: 1,
          oneToOne: 1,
          reset: 1,
          prev: 0,
          play: 0,
          next: 0,
          rotateLeft: 1,
          rotateRight: 1,
          flipHorizontal: 1,
          flipVertical: 1
        },
        className: 'ModalPreview__viewer'
      }
    }
  },

  computed: {
    ...mapGetters('modal', [
      'visibleByModal',
      'fileByModal'
    ]),
    file () {
      return this.fileByModal('preview')
    },
    visible () {
      return this.visibleByModal('preview')
    },
    fileSize () {
      return this.sizeToString(this.file.size, 2)
    },
    modificationDate () {
      const options = {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }
      let date = new Date(this.file.updatedAtMillis)

      return date.toLocaleString('en-GB', options)
    },
    presignedUrl () {
      return this.file.presignedUrl
    }
  },

  watch: {
    visible (visible) {
      if (visible) {
        this.addListeners()
        this.setLoading(this.file)
        this.preparePresignedUrl(this.file)
      } else {
        this.removeListeners()
      }
    }
  },

  beforeDestroy () {
    this.removeListeners()
  },

  methods: {
    ...mapActions('files', [
      'downloadFilePromise'
    ]),
    ...mapActions('modal', [
      'hideModal'
    ]),
    setLoading (item) {
      this.loading = (item.type === 3)
    },
    preparePresignedUrl (item) {
      if (item.presignedUrl) return

      this.downloadFilePromise(item)
        .then((presignedUrl) => {
          item.presignedUrl = presignedUrl
          this.$forceUpdate() // this is required to rerender img/audio/video
        }).catch(() => {})
    },
    loaded () {
      this.loading = false
    },
    errorMessage () {
      Message({
        type: 'error',
        message: 'Oops, we were unable to preview this file',
        showClose: true
      })
    },
    hidePreview () {
      this.hideModal('preview')
    },
    escapeKeyListener (e) {
      if (e.keyCode === 27 && this.visible) this.hidePreview()
    },
    addListeners () {
      document.addEventListener('keydown', this.escapeKeyListener)
    },
    removeListeners () {
      document.removeEventListener('keydown', this.escapeKeyListener)
    }
  }
}
</script>

<style lang="scss">
@import 'ModalPreview';
</style>
